import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '@lsq-frontend/angular-shared/api-service';
import { RelationshipInformation } from '@lsq-frontend/remote-services/enterprise/common';
import { GetRelationshipsByClientService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    DatePickerConfiguration,
    forLifeOf,
    ITemplatePointer,
    LifecycleAware,
    Orientation
} from '@lsq-frontend/common-ui';
import {
    getNavigationItemForNode,
    getRouterLinkById,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { of } from 'rxjs';
import { first } from 'rxjs/operators';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import { RouteParams } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    customerAtlas,
    CUSTOMER_AGING,
    CUSTOMER_COLLECTIONS,
    CUSTOMER_INELIGIBLES,
    CUSTOMER_OVERVIEW,
    CUSTOMER_PURCHASES
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { ICustomerDetailBannerData } from './customer-detail-banner-data';
import { CustomerDetailService } from './customer-detail.service';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';

@LifecycleAware()
@Component({
    selector: 'customer-detail',
    template: `
        <lsq-circle-loader *ngIf="!relationshipInformation"></lsq-circle-loader>
        <div *ngIf="!!relationshipInformation" id="customer-detail">
            <lsq-banner
                [titleText]="title"
                [reportName]="reportName"
                [analyticsName]="analyticsName"
                [service]="apiService"
                [search]="true"
                [searchService]="getRelationshipsByClientService"
                [datePickerConfiguration]="datePickerConfiguration"
                [subTitle]="lastUpdatedDate | lastUpdatedDate"
                [navigationItemsStream]="navigationItemsStream"
                [hasContent]="bannerHasContent()"
                [hideExport]="customerDetailService.hideExport"
                [contentSide]="
                    customerDetailService.bannerData
                        | cacheValue: rightOrLeft:(responsiveService.smBelow | async)
                "
                [stackEagerly]="bannerShouldStackEagerly()"
                [truncateTitle]="true"
                (newCustomerSelected)="selectNewCustomer($event)"
            >
                <ng-container *ngIf="showBannerContentTemplate()">
                    <ng-container *ngIf="leftBannerContentTemplate">
                        <ng-container
                            *ngTemplateOutlet="
                                leftBannerContentTemplate.template;
                                context: { relationshipInformation: relationshipInformation }
                            "
                        ></ng-container>
                    </ng-container>
                    <ng-container *ngIf="rightBannerContentTemplate">
                        <ng-container
                            *ngTemplateOutlet="
                                rightBannerContentTemplate.template;
                                context: { relationshipInformation: relationshipInformation }
                            "
                        ></ng-container>
                    </ng-container>
                </ng-container>
            </lsq-banner>

            <router-outlet></router-outlet>
        </div>
    `,
    styleUrls: ['./customer-detail.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CustomerDetailComponent implements OnInit, OnDestroy {
    public relationshipInitializing = true;
    public isSmallScreen: boolean;
    public relationshipInformation: RelationshipInformation;

    public navigationItemsStream = of([
        getNavigationItemForNode(customerAtlas, CUSTOMER_OVERVIEW),
        getNavigationItemForNode(customerAtlas, CUSTOMER_AGING),
        getNavigationItemForNode(customerAtlas, CUSTOMER_COLLECTIONS),
        getNavigationItemForNode(customerAtlas, CUSTOMER_PURCHASES),
        getNavigationItemForNode(customerAtlas, CUSTOMER_INELIGIBLES)
    ]);

    public color = Color;
    public icons = Icons;
    public orientation = Orientation;

    constructor(
        private activatedRoute: ActivatedRoute,
        private analyticsService: AnalyticsService,
        private router: Router,
        public responsiveService: ResponsiveService,
        public customerDetailService: CustomerDetailService,
        public getRelationshipsByClientService: GetRelationshipsByClientService
    ) {}

    public ngOnInit(): void {
        this.customerDetailService.relationshipInformationStream
            .pipe(forLifeOf(this))
            .subscribe((relationshipInformation) => {
                this.relationshipInformation = relationshipInformation;
                this.relationshipInitializing = false;
            });

        this.activatedRoute.params.pipe(forLifeOf(this)).subscribe((params) => {
            if (
                !!this.relationshipInformation &&
                params.debtorKey !== this.relationshipInformation.debtorKey
            ) {
                this.relationshipInformation = null;
            }
            this.relationshipInitializing = true;
            this.customerDetailService.initCustomer(+params.debtorKey);
        });
    }

    public ngOnDestroy(): void {
        // Wipe the Banner details, otherwise we will briefly see stale data on next visit.
        this.customerDetailService.updateBanner({
            debtorKey: null
        });
    }

    public showBannerContentTemplate(): boolean {
        return (
            !this.isSmallScreen &&
            ((!!this.leftBannerContentTemplate && !!this.leftBannerContentTemplate.template) ||
                (!!this.rightBannerContentTemplate && !!this.rightBannerContentTemplate.template))
        );
    }

    public getBannerData(): ICustomerDetailBannerData {
        return this.customerDetailService.bannerData;
    }

    public bannerHasContent(): boolean {
        return (
            this.showBannerContentTemplate() ||
            !!(this.getBannerData() && this.getBannerData().datePickerConfiguration)
        );
    }

    public bannerShouldStackEagerly(): boolean {
        return (
            !!this.getBannerData() &&
            this.getBannerData().reportId ===
                getRouterLinkById(customerAtlas, CUSTOMER_COLLECTIONS, { relative: true }).join('/')
        );
    }

    public async selectNewCustomer(customerKey: number): Promise<void> {
        this.analyticsService.triggerSimpleEvent(AnalyticsValue.CustomerSwitchedDetails);
        this.customerDetailService.updateBanner({ debtorKey: null });

        // Grab the customer id from the activated route so we can navigate to the new customer's report.
        this.activatedRoute.params.pipe(first()).subscribe(async (params) => {
            const id = params[RouteParams.DebtorKey.substring(1)];
            await this.router.navigateByUrl(this.router.url.replace(id, `${customerKey}`));
        });
    }

    // Getters for banner data.
    public get title(): string | undefined {
        return this.getBannerData() ? this.getBannerData().title : undefined;
    }

    public get reportName(): string | undefined {
        return this.getBannerData() ? this.getBannerData().reportName : undefined;
    }

    public get analyticsName(): string | undefined {
        return this.getBannerData() ? this.getBannerData().analyticsName : undefined;
    }

    public get apiService(): ApiService<any, any> | undefined {
        return this.getBannerData() ? this.getBannerData().apiService : undefined;
    }

    public get datePickerConfiguration(): DatePickerConfiguration | undefined {
        return this.getBannerData() ? this.getBannerData().datePickerConfiguration : undefined;
    }

    public get lastUpdatedDate(): Date | undefined {
        return this.getBannerData() ? this.getBannerData().lastUpdatedDate : undefined;
    }

    public get leftBannerContentTemplate(): ITemplatePointer | undefined {
        return this.getBannerData() ? this.getBannerData().leftContentTemplate : undefined;
    }

    public get rightBannerContentTemplate(): ITemplatePointer | undefined {
        return this.getBannerData() ? this.getBannerData().rightContentTemplate : undefined;
    }

    public rightOrLeft(bannerData: ICustomerDetailBannerData, isSmallScreen): string {
        if (
            !isSmallScreen &&
            bannerData &&
            bannerData.leftContentTemplate &&
            bannerData.leftContentTemplate.template
        ) {
            return 'left';
        }
        return 'right';
    }
}
